import { useEffect } from 'react';
import {
  ArrowRightOutlined,
  LineChartOutlined,
  LockOutlined,
  PrinterOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Flex, Grid, Typography, theme } from 'antd';
import { useNavigate } from 'react-router';
import { estiloDeDestaque, Logotipo, primeiroNome, saudacao } from '@titularis/ui';
import { api } from '../api';
import { useSession } from '../auth';
import { registrarTela } from '../analytics';
import { GRANDES_MODULOS } from '../grandesModulos';
import { ICONE_MODULO } from '../layout/AppLayout';
import { EstadoDaConta } from '../components/EstadoDaConta';
import { NoticiasRecentes } from '../components/NoticiasRecentes';
import { contar } from '../texto';
import { enderecoDasProvas } from '../site';

/**
 * A tela de início.
 *
 * **Era uma vitrine de nove cartões**, agrupados por concurso, e em 05/09/2026
 * ela tinha dois vivos e sete cinzas: Magistratura e OAB não têm prova
 * cadastrada, e a 1ª e a 2ª fase de Cartórios passaram a ficar trancadas
 * enquanto estão sendo comentadas. Uma parede de "em breve" não ajuda ninguém a
 * estudar hoje, e a barra lateral já leva a tudo o que existe.
 *
 * **A forma agora é a da tela inicial do Claude**, que o Calebe indicou: um
 * bloco só, centrado na vertical, com a marca ao lado de uma saudação em
 * serifa, **uma** ação principal logo abaixo, e atalhos pequenos embaixo dela.
 * O que ela ganha não é beleza - é foco: a pessoa abre o app com uma pergunta
 * ("o que eu faço agora?") e a tela responde com uma frase e um botão, em vez
 * de nove opções das quais sete estão fechadas.
 *
 * A frase do dia é o único enfeite, e é de propósito: quem estuda para concurso
 * volta todo dia à mesma tela, e uma linha que muda é o que a impede de virar
 * papel de parede.
 *
 * **As Notícias Recentes entraram embaixo, e a tela continua sendo um bloco
 * só.** É o rodapé de recentes do Claude: três fileiras baixas, com a capa da
 * notícia, na mesma coluna de 520px do cartão da ação principal - o alinhamento
 * das quatro bordas é o que impede a seção de ler como outra tela colada nesta.
 * Elas levam para o site, que é onde a notícia mora (ver `site.ts`).
 *
 * O conjunto inteiro - saudação, ação, atalhos e notícias - é que se centraliza
 * na vertical, e não a saudação sozinha: com a saudação centrada e as notícias
 * empurradas para o fim da coluna, sobrava um vão de trezentos pixels no meio
 * da tela, e as duas metades pareciam duas telas.
 */

/**
 * As frases, uma por dia.
 *
 * Escritas no tom do resto do produto - seco, específico, sem exclamação e sem
 * promessa. "Você consegue!" é o que toda plataforma diz; o que um concurseiro
 * de cartório reconhece como verdade é o detalhe do ofício dele.
 *
 * Ficam no código, e não numa tabela: são oito linhas de texto que mudam quando
 * alguém tiver uma ideia melhor, e uma tabela para elas seria tela, seed e
 * migration para economizar um deploy.
 */
const FRASES = [
  'Quem passa é quem volta na questão que errou.',
  'A prova não cobra o que você leu. Cobra o que você lembra.',
  'O edital é grande. O dia de hoje não é.',
  'Constância vence intensidade.',
  'Errar aqui é mais barato que errar na prova.',
  'Ninguém decora a lei inteira. Decora-se o que já caiu.',
  'Comentário lido é questão que não se erra duas vezes.',
  'Uma questão hoje já é mais do que ontem.',
];

/**
 * A frase de hoje, a mesma para todo mundo.
 *
 * O dia vem do servidor (fuso de Brasília, o mesmo da cota), e não do relógio
 * do aparelho: assim a frase vira à meia-noite junto com as questões, e não
 * troca no meio da tarde de quem está com o fuso errado. Sem o dia ainda, cai
 * na primeira - qualquer frase serve melhor que um espaço vazio piscando.
 */
function fraseDoDia(dia: string): string {
  if (!dia) return FRASES[0]!;
  const soma = [...dia].reduce((total, letra) => total + letra.charCodeAt(0), 0);
  return FRASES[soma % FRASES.length]!;
}

/**
 * A coluna da tela: o cartão da ação e as notícias têm a **mesma** largura.
 *
 * É uma constante, e não dois números iguais, porque o alinhamento entre as
 * duas peças é o que faz a seção nova parecer parte do bloco. Dois `520`
 * escritos separados viram 520 e 560 no primeiro ajuste de qualquer um deles.
 */
const LARGURA_DA_COLUNA = 520;

export function HomePage() {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const telas = Grid.useBreakpoint();
  const { data: sessao } = useSession();

  /*
   * Sem nome, a saudação vira só "Boa noite" - e não "Boa noite, ." nem um
   * espaço pendurado. Acontece no primeiro render, antes de a sessão chegar, e
   * em conta do Google sem nome no perfil.
   */
  const nome = primeiroNome(sessao?.user.name);

  useEffect(() => registrarTela('/'), []);

  const status = useQuery({ queryKey: ['study-status'], queryFn: () => api.studyStatus() });
  const desempenho = useQuery({
    queryKey: ['study-performance'],
    queryFn: () => api.studyPerformance(),
  });

  /*
   * Quantas questões o Enac tem. Uma página de um item só, porque o que
   * interessa é o `total` do cabeçalho - e o catálogo cresce devagar, então
   * meia hora de validade poupa a consulta a cada visita à Home.
   */
  const acervo = useQuery({
    queryKey: ['study-total', 'enac'],
    queryFn: () => api.listStudyQuestions({ module: 'enac', pageSize: 1 }),
    staleTime: 30 * 60 * 1000,
  });

  const jaEstudou = (desempenho.data?.total ?? 0) > 0;
  const noAr = GRANDES_MODULOS.flatMap((concurso) =>
    concurso.disponivel ? concurso.subModulos : [],
  );
  /*
   * As fases abertas além do Enac, que já é a ação principal. Saem do mesmo
   * `GRANDES_MODULOS` da barra: a fase que abre lá aparece aqui no mesmo
   * deploy, sem uma segunda lista para esquecer.
   */
  const outrasFases = noAr.filter(
    (sub) => sub.moduleKey && sub.moduleKey !== 'enac' && !sub.emPreparo,
  );
  const emPreparo = noAr.filter((sub) => sub.emPreparo);

  return (
    /*
     * Centrado de verdade: a coluna de leitura do `AppLayout` estica até o fim
     * da tela, então `flex: 1` aqui dá a altura toda - sem adivinhar quanto a
     * faixa do plano ocupa.
     *
     * A centralização é `marginBlock: auto` no bloco de dentro, e **não**
     * `justify="center"` aqui. A diferença aparece quando o conteúdo fica mais
     * alto que a tela, que é o que as notícias fizeram no celular deitado: com
     * `justify`, o excedente vaza pelos dois lados e o topo do bloco fica
     * inalcançável na rolagem; com a margem automática, ela colapsa para zero e
     * a tela rola como qualquer outra.
     */
    <Flex vertical style={{ flex: 1, paddingInline: 8 }}>
      <Flex
        vertical
        align="center"
        gap={28}
        style={{ width: '100%', marginBlock: 'auto', paddingBlock: 24, textAlign: 'center' }}
      >
        <div>
          {/*
            A marca ao lado da saudação, como o asterisco do Claude. Ela é o único
            lugar da tela onde o produto se apresenta - o resto é do aluno.
          */}
          {/* A serifa encolhe no celular: 38px numa tela de 390 come duas linhas
              e empurra a ação principal para baixo da dobra. */}
          <Flex align="center" justify="center" gap={10} style={{ marginBottom: 10 }}>
            <Logotipo apenasSimbolo altura={telas.sm ? 30 : 24} />
            <h1
              style={{
                ...estiloDeDestaque(telas.sm ? 38 : 29, 300),
                margin: 0,
                color: token.colorText,
              }}
            >
              {saudacao()}
              {nome ? `, ${nome}` : ''}
            </h1>
          </Flex>

          <Typography.Text type="secondary" style={{ fontSize: 15 }}>
            {fraseDoDia(status.data?.dia ?? '')}
          </Typography.Text>

          {/*
            O estado da conta, logo abaixo da frase: é o único lugar da Home
            onde ter comprado aparece, e a diferença entre as duas contas
            passa a ser por acréscimo, não pela ausência dos avisos de limite.
          */}
          <Flex justify="center" style={{ marginTop: 12 }}>
            <EstadoDaConta status={status.data} desempenho={desempenho.data} />
          </Flex>
        </div>

        <AcaoPrincipal
          titulo={jaEstudou ? 'Continuar no Enac' : 'Começar pelo Enac'}
          descricao={
            acervo.data
              ? `${contar(acervo.data.total, 'questão comentada', 'questões comentadas')}, com resolução artigo por artigo.`
              : 'Questões comentadas, com resolução artigo por artigo.'
          }
          aoClicar={() => void navigate('/estudar?module=enac')}
        />

        {/*
          Os atalhos em grade, e não numa fila que se enrola sozinha.

          **A fila tinha 645px de largura contra os 520 do cartão**, medidos no
          navegador: a coisa menos importante da tela era a mais larga dela, e
          isso é hierarquia invertida. Os quatro rótulos não cabem numa fila de
          520 - eles somam 621 mais os vãos -, então a saída é a grade: duas
          colunas onde há espaço, uma no celular.

          **Uma coluna no celular, e não duas apertadas:** numa tela de 360 a
          célula fica com 152px e "Legislação Comentada" precisa de 163, então
          duas colunas cortariam o nome do módulo. Alvo largo também é o que o
          polegar acha.
        */}
        <div
          style={{
            width: '100%',
            maxWidth: LARGURA_DA_COLUNA,
            display: 'grid',
            gridTemplateColumns: telas.sm ? '1fr 1fr' : '1fr',
            gap: 8,
          }}
        >
          {/*
            As fases abertas vêm primeiro: são lugar de estudar questão, como a
            ação principal logo acima, e o resto da grade são ferramentas.
          */}
          {outrasFases.map((sub) => (
            <Atalho
              key={sub.chave}
              icone={ICONE_MODULO[sub.moduleKey!]}
              rotulo={sub.nome}
              aoClicar={() => void navigate(`/estudar?module=${sub.moduleKey}`)}
            />
          ))}
          <Atalho
            icone={<ReadOutlined />}
            rotulo="Legislação Comentada"
            aoClicar={() => void navigate('/legislacao')}
          />
          <Atalho
            icone={<LineChartOutlined />}
            rotulo="Desempenho"
            aoClicar={() => void navigate('/configuracoes/desempenho')}
          />
          {/*
            Provas para imprimir abre no site, e não aqui: a página é pública e
            já existe do outro lado, indexável e com prévia de link. É o mesmo
            argumento que manteve a notícia sem tela própria no app.
          */}
          <Atalho
            icone={<PrinterOutlined />}
            rotulo="Provas para imprimir"
            aoClicar={() => window.open(enderecoDasProvas, '_blank', 'noopener,noreferrer')}
          />

          {/*
            As etapas que ainda não abriram entram como atalho apagado, e não
            somem: o aluno precisa saber que elas vêm, e a palavra "comentando"
            diz por que ainda não estão aqui. Sem elas, quem comprou pensando na
            2ª fase acharia que a plataforma não a cobre.
          */}
          {emPreparo.map((sub) => (
            <Atalho key={sub.chave} icone={<LockOutlined />} rotulo={`${sub.nome} · comentando`} />
          ))}
        </div>

        {/*
          O respiro extra separa o que é ação do que é leitura. Com o mesmo vão
          dos atalhos, a primeira fileira de notícia lia como mais um atalho, só
          que retangular.
        */}
        <div style={{ width: '100%', maxWidth: LARGURA_DA_COLUNA, marginTop: 20 }}>
          <NoticiasRecentes />
        </div>
      </Flex>
    </Flex>
  );
}

/**
 * A ação principal: uma só, larga, e no lugar onde o Claude põe o campo de
 * texto. Duas ações lado a lado aqui obrigariam a escolher antes de começar.
 *
 * **Continua sendo o Enac com a 1ª fase aberta** (15/10/2026): é a prova que todo
 * candidato faz antes das estaduais e o maior acervo comentado. A 1ª fase entra
 * como atalho, logo abaixo.
 */
function AcaoPrincipal({
  titulo,
  descricao,
  aoClicar,
}: {
  titulo: string;
  descricao: string;
  aoClicar: () => void;
}) {
  const { token } = theme.useToken();

  return (
    <Flex
      align="center"
      justify="space-between"
      gap={16}
      role="button"
      tabIndex={0}
      onClick={aoClicar}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter' || evento.key === ' ') {
          evento.preventDefault();
          aoClicar();
        }
      }}
      style={{
        width: '100%',
        maxWidth: LARGURA_DA_COLUNA,
        padding: '18px 22px',
        cursor: 'pointer',
        textAlign: 'start',
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
        boxShadow: token.boxShadowTertiary,
      }}
    >
      <Flex vertical gap={2} style={{ minWidth: 0 }}>
        <Typography.Text strong style={{ fontSize: 16 }}>
          {titulo}
        </Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {descricao}
        </Typography.Text>
      </Flex>
      <ArrowRightOutlined style={{ color: token.colorPrimary, fontSize: 16, flexShrink: 0 }} />
    </Flex>
  );
}

/**
 * Um atalho: uma célula da grade, com o canto do resto do produto.
 *
 * **Era uma pílula**, copiada do rodapé da tela inicial do Claude, e o raio 999
 * era o único do produto - o tema tem 8 para controle e 16 para cartão e modal.
 * Pílula, aqui, é vocabulário de **etiqueta**: a barra lateral usa uma
 * (`Tag`) para dizer "em breve" ao lado de Magistratura e OAB. Com a mesma
 * forma, "1ª fase · comentando" lia como um rótulo de estado pendurado na tela,
 * e não como um lugar onde se clica.
 *
 * O `borderRadius` do tema é o que resolve isso e é o que impede a divergência
 * de voltar: se o raio dos controles mudar um dia, este muda junto.
 */
function Atalho({
  icone,
  rotulo,
  aoClicar,
}: {
  icone: React.ReactNode;
  rotulo: string;
  /** Sem clique, o atalho é informativo - o caso das etapas sendo comentadas. */
  aoClicar?: () => void;
}) {
  const { token } = theme.useToken();
  const clicavel = Boolean(aoClicar);

  return (
    <Flex
      align="center"
      justify="center"
      gap={8}
      {...(clicavel ? { role: 'button', tabIndex: 0, onClick: aoClicar } : {})}
      onKeyDown={(evento) => {
        if (clicavel && (evento.key === 'Enter' || evento.key === ' ')) {
          evento.preventDefault();
          aoClicar!();
        }
      }}
      style={{
        paddingBlock: 7,
        paddingInline: 14,
        borderRadius: token.borderRadius,
        cursor: clicavel ? 'pointer' : 'default',
        border: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
        color: clicavel ? token.colorText : token.colorTextTertiary,
        fontSize: token.fontSizeSM,
        // O rótulo é o nome de um módulo: ele não quebra em duas linhas nem
        // desalinha a altura da célula vizinha.
        whiteSpace: 'nowrap',
      }}
    >
      {icone}
      {rotulo}
    </Flex>
  );
}
